import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {fromJS} from 'immutable';
import moment from 'moment';
import {Link} from 'react-router';

import {Anchor, Dropdown, IconButton, Card, SplitCard, Text, Heading} from '@albert-io/atomic';

import {callAction, getStoreByName} from 'client/framework';
import {renderMoment} from 'lib/timeUtils';
import {pluralize} from 'lib/stringUtils';
import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import RadioButtonGroup from 'generic/RadioButtonGroup/RadioButtonGroup.react';
import LineChart from 'generic/LineChart/LineChart.react';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import SelectableDateRange from 'generic/SelectableDateRange/SelectableDateRange.react';
import {dateRangeOptions, RANGES} from 'generic/SelectableDateRange/shared';
import withTracking from 'lib/hocs/withTracking';
import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';

import schoolAdministrationStore from '../../SchoolAdministrationStore';
import {
  NotifyTeachersMessage,
  NotifyTeachersDropdownItem
} from '../NotifyTeachers/NotifyTeachers.react';

import dashboardActions from './Dashboard.actions';
import dashboardStore from './Dashboard.store';
import SeatUsageGraph from './Graphs/SeatUsage.react';

import './dashboard.scss';

const TrackedLink = withTracking(Anchor, {
  onClick: {
    event: 'click',
    type: 'hyperlink',
    feature: 'admin dashboard',
    component: 'getting started with albert',
    content: (e, props) => props.children
  }
});

const buttonSelections = fromJS([
  {
    label: 'Day',
    value: 'day'
  },
  {
    label: 'Week',
    value: 'week'
  },
  {
    label: 'Month',
    value: 'month'
  }
]);

const dateRanges = dateRangeOptions
  .filterNot((range) => {
    return ['All-time', 'Last week', 'Last month'].includes(range.get('name'));
  })
  .map((range) => {
    return range.get('name') === 'Custom' ? range : range.set('shouldHideDateRangeSelector', true);
  });

export default class Dashboard extends React.Component {
  UNSAFE_componentWillMount() {
    this.schoolId = schoolAdministrationStore.getActiveSchoolId();
    callAction(dashboardActions.SET_SCHOOL_ID, this.schoolId);
  }

  UNSAFE_componentWillUpdate() {
    if (this.schoolId !== schoolAdministrationStore.getActiveSchoolId()) {
      this.schoolId = schoolAdministrationStore.getActiveSchoolId();
      callAction(dashboardActions.SET_SCHOOL_ID, this.schoolId);
    }
  }

  setSelectedInterval() {
    const radioButtonGroup = getStoreByName('lineChartButtonGroupStore');
    const selectedInterval = radioButtonGroup ? radioButtonGroup.getSelectedItem() : 'month';
    callAction(dashboardActions.SET_SELECTED_INTERVAL, selectedInterval);
  }

  getUsageCard(school) {
    const currentTime = systemTimeOffsetStore.getCurrentTime();
    const schoolMeta = school.getMeta();
    const expirationDate = schoolMeta.getLicenseExpirationDate();
    const startDate = schoolMeta.getLicenseStartDate();
    const isExpired = moment(expirationDate).isBefore(currentTime);
    const isScheduled = moment(startDate).isAfter(currentTime);
    const licenseType = schoolMeta.getLicenseType();
    const isActive = schoolMeta.isLicenseActive();
    const shouldShowSeatUsage = Boolean(schoolMeta.getCountOfAvailableSeats()) && isActive;

    return shouldShowSeatUsage ? (
      <SplitCard className='sa-seat-usage u-pad_3'>
        <SplitCard.Section paddingLevel={0}>
          <div className='sa-seat-usage-title u-mar-b_1'>
            <Text bold>Seat Usage</Text>
            <div className='sa-seat-usage-title__menu'>
              <Dropdown
                withMinWidth
                trigger={
                  <IconButton
                    onClick={(e) => e.preventDefault()}
                    icon={['far', 'bell']}
                    label='Dropdown'
                  />
                }
              >
                <NotifyTeachersDropdownItem school={school} />
              </Dropdown>
            </div>
          </div>
          <div className='sa-seat-usage-title__message'>
            <NotifyTeachersMessage school={school} />
          </div>
        </SplitCard.Section>
        <SplitCard.Section className='sa-seat-usage__d3-wrapper u-pad-t_2' paddingLevel={0}>
          <SeatUsage
            expirationDate={expirationDate}
            licenseType={schoolMeta.getLicenseType()}
            seatsTotal={schoolMeta.getCountOfAvailableSeats()}
            seatsUsedTotal={schoolMeta.getCountOfStudents()}
          />
        </SplitCard.Section>
      </SplitCard>
    ) : (
      <NoActiveLicenseCard
        isExpired={isExpired}
        isScheduled={isScheduled}
        licenseType={licenseType}
        startDate={startDate}
      />
    );
  }

  render() {
    if (!dashboardStore.hasSchoolAdminStatistics()) {
      return <LoadingIndicator />;
    }

    const school = dashboardStore.getCountOfPendingTeachersQuery().getResource();
    const schoolMeta = school.getMeta();
    const dashboardCharts = dashboardStore.hasSchoolAdminChartData() ? (
      <DashboardCharts />
    ) : (
      <LoadingIndicator />
    );
    return (
      <div className='school-admin-dashboard u-flex-grow_1 u-pad-lr_4 u-pad-tb_6 u-bgc_slate-100'>
        {school.getMeta().getPopulatedMetadataFields() && school.isHardCap() && (
          <HardCapWarningBanner
            className='u-mar-b_2'
            rounded
            admin
            seatsRemaining={
              school.getMeta().getCountOfAvailableSeats() - school.getMeta().getCountOfStudents()
            }
          />
        )}
        <div className='school-admin-dashboard-stats'>
          <div className='school-admin-dashboard-stats__usage'>{this.getUsageCard(school)}</div>
          <div className='school-admin-dashboard-stats__counts'>
            <div className='school-admin-dashboard-stats__list u-pad-t_3 u-pad-b_2 u-pad-lr_2 u-border-color_slate-200 u-border u-bgc_white'>
              <Text bold className='u-pad-l_1'>
                Usage Summary
              </Text>
              <StatCard
                to={`/school-admin/${this.schoolId}/usage-reports/teachers`}
                title='Teachers'
                count={schoolMeta.getCountOfApprovedTeachers()}
                content={
                  <div>
                    <div className='u-mar-r_2'>Approved to access your school’s license.</div>
                    {!!schoolMeta.getCountOfTeachers() && (
                      <div className='u-mar-t_1 u-color_yellow-700 u-border u-border-color_yellow-300 u-bgc_yellow-050 u-border-radius_1 u-pad_1 u-display_inline-flex'>
                        {schoolMeta.getCountOfTeachers()} pending{' '}
                        {pluralize('teacher', schoolMeta.getCountOfTeachers())}
                        {pluralize(' is', schoolMeta.getCountOfTeachers(), ' are')} waiting for your
                        approval.&nbsp;
                        <Anchor
                          inheritColor
                          underlined
                          to={`/school-admin/${this.schoolId}/manage-staff/pending-approval`}
                        >
                          View
                        </Anchor>
                      </div>
                    )}
                  </div>
                }
              />
              <StatCard
                title='Admins'
                count={schoolMeta.getCountOfApprovedAdmins()}
                content='Approved to access your school’s admin dashboard.'
              />
              <StatCard
                to={`/school-admin/${this.schoolId}/usage-reports/classes`}
                title='Classrooms'
                count={schoolMeta.getCountOfClassrooms()}
                content='Classes owned by approved teachers.'
              />
              <StatCard
                to={`/school-admin/${this.schoolId}/usage-reports/students`}
                title='Students'
                count={schoolMeta.getCountOfStudents()}
                content='Enrolled in active classes.'
              />
              <StatCard
                to={`/school-admin/${this.schoolId}/usage-reports/subjects`}
                title='Subjects'
                count={schoolMeta.getCountOfSubjects()}
                content='Currently included in all approved teachers’ classrooms.'
              />
            </div>
          </div>
        </div>
        <Card paddingLevel={3} className='school-admin-dashboard__charts u-mar-t_2'>
          <div className='school-admin-dashboard__date-selections'>
            <SelectableDateRange
              storeName={dashboardStore.getSelectableDateRangeStoreName()}
              className='school-admin-dashboard__date-range'
              options={dateRanges}
              defaultDateRange={RANGES.THIS_SCHOOL_YEAR}
            />
            <RadioButtonGroup
              className='school-admin-dashboard__chart-buttons'
              color='gray'
              data={buttonSelections}
              defaultValue={dashboardStore.getSelectedInterval()}
              name='school-admin-line-charts'
              storeName='lineChartButtonGroupStore'
              onChange={this.setSelectedInterval}
            />
          </div>
          {dashboardCharts}
        </Card>
        <GettingStartedModule />
      </div>
    );
  }
}

const GettingStartedModule = () => (
  <div className='school-admin-dashboard__getting-started u-border u-border-color_slate-300 u-bgc_white u-pad_4'>
    <div className='u-display_flex u-align-items_center u-mar-b_3'>
      <Text size='m' bold className='u-flex-grow_1 u-mar-r_2'>
        Getting Started With Albert
      </Text>
    </div>
    <iframe
      allowFullScreen
      className='u-width_100pc u-mar-b_3 u-overflow_hidden u-border-radius_1'
      title='Albert Admin Webinar'
      src='https://www.youtube.com/embed/qsjTwDw5gkw?si=rOKo2ExQbBFJC_96'
    />
    <Text size='m' as='div' className='u-display_flex u-flex-direction_column u-mar-b_3'>
      {helpItems.map((item) => (
        <TrackedLink
          className='u-mar-b_1'
          target='_blank'
          rel='noopener noreferrer'
          href={item.href}
        >
          {item.title}
        </TrackedLink>
      ))}
    </Text>
    <Text size='m'>
      Not finding what you need? Search our{' '}
      <TrackedLink
        target='_blank'
        rel='noopener noreferrer'
        href='https://help.albert.io/en/collections/521207-admin'
      >
        Help Center
      </TrackedLink>
      .
    </Text>
  </div>
);

const helpItems = [
  {
    title: 'Admin Onboarding',
    href: 'https://help.albert.io/en/articles/1884976-admin-onboarding-getting-started'
  },
  {
    title: 'Manage staff',
    href: 'https://help.albert.io/en/articles/2190890-how-do-i-manage-staff-from-the-admin-dashboard'
  },
  {
    title: 'Find your seat usage',
    href: 'https://help.albert.io/en/articles/2683834-where-can-i-find-my-school-s-usage-data'
  },
  {
    title: 'Provide ACT® or SAT® practice to students',
    href: 'https://help.albert.io/en/articles/6554488-how-do-i-provide-students-with-act-and-sat-access'
  },
  {
    title: `View a teacher’s classroom`,
    href: 'https://help.albert.io/en/articles/4454444-how-do-i-view-a-teacher-s-class-as-an-admin'
  },
  {
    title: 'Learn about school accounts',
    href: 'https://help.albert.io/en/articles/4433539-what-are-school-created-accounts'
  },
  {
    title: 'Integrate with LMS',
    href: 'https://help.albert.io/en/articles/1885028-do-you-integrate-with-any-lms-systems'
  },
  {
    title: 'Explore Professional Development options',
    href: 'https://docsend.com/view/tgjpfmweu3325atf'
  }
];

const StatCard = ({to, title, count, content}) => {
  const StatCardTag = to ? Link : 'div';

  return (
    <StatCardTag
      className={classnames('school-admin-dashboard__stat u-pad_1', {
        'school-admin-dashboard__stat--link u-display_block': !!to
      })}
      to={to}
    >
      <div className='u-display_flex u-align-items_flex_start'>
        <div className='u-flex-grow_1 u-mar-r_4 school-admin-dashboard__stat-title'>
          <Text bold size='m' as='div'>
            {title}
          </Text>
          <Text emphasis='lowest' size='s' as='div'>
            {content}
          </Text>
        </div>
        <Heading className='school-admin-dashboard__count' size='m'>
          {count}
        </Heading>
      </div>
    </StatCardTag>
  );
};

StatCard.propTypes = {
  to: PropTypes.string,
  title: PropTypes.string,
  count: PropTypes.number,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};

const NoActiveLicenseCard = ({isExpired, isScheduled, licenseType, startDate}) => {
  let copy = {
    header: 'Interested in a license for your school?',
    smallText: 'Contact our sales team.',
    email: 'mailto:schools@albert.io'
  };

  if (isExpired && licenseType !== 'pilot') {
    copy = {
      header: 'Your license has expired!',
      smallText: 'Please contact our success team to start your renewal.',
      email: 'mailto:success@albert.io'
    };
  }

  if (isScheduled && licenseType !== 'demo') {
    const startDateString = renderMoment(startDate, 'date');
    copy = {
      header: `Your license doesn't start until ${startDateString}.`,
      smallText: 'To start your license sooner, contact our success team.',
      email: 'mailto:success@albert.io'
    };
  }

  return (
    <Card paddingLevel={2} className='usage-card usage-card--empty'>
      <div className='usage-card-message'>
        <h2>{copy.header}</h2>
        <Anchor href={copy.email}>{copy.smallText}</Anchor>
      </div>
    </Card>
  );
};

NoActiveLicenseCard.propTypes = {
  isExpired: PropTypes.bool,
  isScheduled: PropTypes.bool,
  licenseType: PropTypes.string,
  startDate: PropTypes.object
};

class SeatUsage extends React.PureComponent {
  static propTypes = {
    licenseType: PropTypes.string.isRequired,
    seatsTotal: PropTypes.number.isRequired,
    seatsUsedTotal: PropTypes.number.isRequired,
    expirationDate: PropTypes.object.isRequired
  };

  render() {
    const aboveMaxSeats = this.props.seatsUsedTotal > this.props.seatsTotal;
    const expirationDate = renderMoment(this.props.expirationDate, 'date', 'America/Chicago');
    return (
      <div className='usage-card'>
        <SeatUsageGraph
          className={classnames('dashboard-seat-usage-graph', {
            'dashboard-seat-usage-graph--warning': aboveMaxSeats
          })}
          value={this.props.seatsUsedTotal}
          total={this.props.seatsTotal}
        />
        <div className='usage-card-stats u-mar-l_4'>
          <div>
            <Heading>{this.props.seatsUsedTotal} used</Heading>
            <Text as='div' emphasis='lowest'>
              {this.props.seatsTotal} purchased
            </Text>
            <ExceededSeatCountMessage
              licenseType={this.props.licenseType}
              seatsTotal={this.props.seatsTotal}
              seatsUsedTotal={this.props.seatsUsedTotal}
            />
          </div>
          <Text emphasis='lowest'>Expires on {expirationDate}</Text>
        </div>
      </div>
    );
  }
}

const ExceededSeatCountMessage = ({licenseType, seatsTotal, seatsUsedTotal}) => {
  const exceededSeatCount = seatsUsedTotal - seatsTotal;
  if (exceededSeatCount < 1) {
    return null;
  }
  const isPilot = licenseType === 'pilot';
  const content = isPilot ? (
    <span>
      Your pilot is only for {seatsTotal} students, but no worries. You can pilot with as many
      students as you would like in order to see if Albert will be a good fit for your school.
    </span>
  ) : (
    <span>
      You have exceeded your licensed seat limit by {exceededSeatCount}{' '}
      {pluralize('seat', exceededSeatCount)}! If you have not already, please contact{' '}
      <Anchor href='mailto:schools@albert.io'>schools@albert.io</Anchor> to avoid having your
      account frozen.
    </span>
  );
  return (
    <Text
      as='div'
      className='exceeded-seat-count-message u-mar-t_2'
      size='s'
      color={isPilot ? 'black' : 'red'}
    >
      {content}
    </Text>
  );
};

ExceededSeatCountMessage.propTypes = {
  licenseType: PropTypes.string,
  seatsTotal: PropTypes.number,
  seatsUsedTotal: PropTypes.number
};

class DashboardCharts extends React.Component {
  chartWrapperEl = React.createRef();

  render() {
    const questionsAnsweredInterval = dashboardStore.getCountOfGuessesIntervals();
    const studentHoursSpentInterval = dashboardStore.getTotalTimeElapsedIntervals();
    let chartWidth;
    if (this.chartWrapperEl.current) {
      chartWidth = this.chartWrapperEl.current.getBoundingClientRect().width + 100;
    }

    let tooltipFormat;
    if (dashboardStore.getSelectedInterval() === 'month') {
      tooltipFormat = 'MMM';
    }

    return (
      <div id='school-admin-dashboard__chart-wrapper' ref={this.chartWrapperEl}>
        <h4 className='u-mar-r_2'>Students - Attempts Made</h4>
        <LineChart
          color='red'
          data={questionsAnsweredInterval}
          storeName='questionsAnsweredInterval'
          tooltipFormat={tooltipFormat}
          tooltipMessage='attempts'
          width={chartWidth}
          selectedInterval={dashboardStore.getSelectedInterval()}
        />
        <h4 className='u-mar-r_2'>Students - Hours spent on site</h4>
        <LineChart
          color='purple'
          data={studentHoursSpentInterval}
          storeName='studentHoursSpentInterval'
          tooltipMessage='hours spent'
          tooltipFormat={tooltipFormat}
          width={chartWidth}
          selectedInterval={dashboardStore.getSelectedInterval()}
        />
      </div>
    );
  }
}
